import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { memo, useMemo, type ReactNode, type HTMLAttributes } from 'react';
import { AnimatePresence, motion, type MotionNodeAnimationOptions } from 'motion/react';

import { useRouteInfo } from '@/hooks/use-route-info';
import { useCreateNoteFromTemplate } from '@/hooks/rxdb-models';
import type { AgentRunActivityState, EditorState } from '@/hooks/llm/use-llm-agent';

import { pluralize } from '@flintmint/core-shared/utils';
import { Icon } from '@flintmint/web-shared/components/icon';
import { Loader } from '@flintmint/web-shared/components/loader';
import { Tooltip } from '@flintmint/web-shared/components/tooltip';
import { Popover } from '@flintmint/web-shared/components/popover';
import { InlineTags } from '@/components/inline-tags/inline-tags';
import { Checkbox } from '@flintmint/web-shared/components/checkbox';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';
import { ColorIndicator } from '@flintmint/web-shared/components/color-indicator';

import { itemTypes } from '@/constants/item-types';
import { animateScaleAndOpacity, tagDefaultColor } from '@flintmint/web-shared/constants/ui';

import { formatDate, getRandomItem } from '@/utils/helper-utils';

import type { SearchableDocument } from '@/services/search-service/orama-schema';
import type { MenuProps } from '@flintmint/web-shared/components/menu';

import styles from './list-item.module.scss';

export const ListItem = memo((props: ListItemProps) => {
  const {
    menu,
    data,
    active,
    loading,
    selected,
    multiline,
    className,
    multiselectOn,
    showDate = true,
    mode = 'document',
    appearance = 'default',
    showDescription = true,
    displayCheckForSelected,
    agentRunActivityState,
    agentRunEditorState,
    ...rest
  } = props;

  const { basePath } = useRouteInfo();
  const { mutate: createNoteFromTemplate, isLoading: isCreatingNote } = useCreateNoteFromTemplate();

  const namePlaceholderWidth = useMemo(() => getRandomItem([160, 118]), []);
  const datePlaceholderWidth = useMemo(() => getRandomItem([106, 70]), []);
  const descriptionPlaceholderWidth = useMemo(() => getRandomItem([210, 136]), []);

  const { type, name, content, updatedAt, numTags } = data || {};

  const showSkeleton = loading || !data;
  const showAgentLoading = ['thinking', 'reading', 'writing'].includes(agentRunActivityState ?? '');
  const leadingIcon = getLeadingIcon(agentRunEditorState, agentRunActivityState, itemTypes[type as keyof typeof itemTypes]?.icon);

  const skeleton = (
    <>
      <div className={styles.listItemContentHolder}>
        <div className={styles.nameHolder}>
          <Skeleton trueWidth={20} trueHeight={20} width={16} height={16} borderRadius={5} />
          <Skeleton height={6} borderRadius={4} width={namePlaceholderWidth} />
          {!!menu && <Skeleton width={12} height={12} borderRadius={4} />}
        </div>
        {showDescription && (
          <div className={styles.descriptionHolder}>
            <Skeleton height={4} borderRadius={4} width={descriptionPlaceholderWidth} />
          </div>
        )}
        {showDate && (
          <div className={styles.date}>
            <Skeleton height={4} trueHeight={14} borderRadius={4} width={datePlaceholderWidth} />
          </div>
        )}
      </div>
    </>
  );

  const animateScaleAndWidth: MotionNodeAnimationOptions = {
    initial: { width: 0, scale: 0 },
    animate: { width: 12, scale: 1 },
    exit: { width: 0, scale: 0 },
    transition: { duration: 0.08, ease: 'easeOut' },
  };

  const renderColorIndicator = () => (
    <motion.div
      key="tag"
      data-testid="color-indicator"
      className={clsx(styles.colorIndicatorHolder, styles.left)}
      {...(multiselectOn ? animateScaleAndWidth : animateScaleAndOpacity)}
    >
      <ColorIndicator size={multiselectOn ? 'tiny' : 'default'} color={data?.content || tagDefaultColor} />
    </motion.div>
  );

  return (
    <li
      aria-selected={selected}
      className={clsx(styles.listItemHolder, className, styles[`a-${appearance}`], styles[`m-${mode}`], {
        'pointer-events-none': loading,
        [styles.active]: !multiselectOn && active,
        [styles.selected]: !multiselectOn && selected,
      })}
      data-name={name}
      data-agent-run-activity={agentRunActivityState}
      data-agent-run-editor={agentRunEditorState}
      {...rest}
    >
      {showSkeleton ? (
        skeleton
      ) : (
        <>
          <Link
            draggable={false}
            to={`${basePath}/${data.id}`}
            className={styles.listItemLink}
            onClick={(e) => e.preventDefault()}
          >
            <div className={styles.listItemContentHolder}>
              <div className={styles.nameHolder}>
                <AnimatePresence mode="wait" initial={false}>
                  {multiselectOn && (displayCheckForSelected ? selected : true) ? (
                    <motion.div key="checkbox" className={styles.left} {...animateScaleAndOpacity}>
                      <Checkbox checked={selected} />
                    </motion.div>
                  ) : mode === 'tag' ? (
                    renderColorIndicator()
                  ) : showAgentLoading ? (
                    <motion.div key="loader" className={styles.left} {...animateScaleAndOpacity}>
                      <Loader size={16} variant="steps" color={active ? 'hero-sc' : 'hero'} data-testid="agent-status-loader" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="icon"
                      className={clsx(styles.left, {
                        [styles.active]: active,
                      })}
                      data-success={leadingIcon.success}
                      data-error={leadingIcon.error}
                      {...animateScaleAndOpacity}
                    >
                      <Icon size={20} type={leadingIcon.type!} className={styles.icon} data-testid={`${type}-icon`} />
                    </motion.div>
                  )}
                </AnimatePresence>
                {mode === 'tag' && <AnimatePresence>{multiselectOn && renderColorIndicator()}</AnimatePresence>}
                <span title={name} className={clsx('ellipsis-text', styles.name)}>
                  {name}
                </span>
                {mode === 'tag' && !!numTags && (
                  <div className={clsx(styles.docsCount, 'chip')}>{pluralize(numTags, 'item')}</div>
                )}
                {!!data.tagIds?.length && <InlineTags tagIds={data.tagIds} />}
                {!!menu && (
                  <Popover placement="bottom" menu={menu(data)} onContentClick={(e) => e.stopPropagation()}>
                    <Icon
                      type="more-horizontal"
                      data-testid="list-menu"
                      className={styles.menuIcon}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    />
                  </Popover>
                )}
                {data.type === 'templates' && (
                  <Tooltip placement="top" content="New Note from Template">
                    <Icon
                      type="keyframes-double-add"
                      data-testid="new-note-from-template-icon"
                      className={clsx('trailing-icon', {
                        'pointer-events-none': isCreatingNote,
                      })}
                      onClick={async (e) => {
                        await createNoteFromTemplate(data.id);
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    />
                  </Tooltip>
                )}
              </div>
              {showDescription && (
                <div
                  className={clsx(styles.descriptionHolder, {
                    [styles.noContent]: !content,
                  })}
                >
                  <span
                    className={clsx({
                      'ellipsis-text': !multiline,
                      [styles.multiline]: multiline,
                    })}
                  >
                    {(content || 'No content yet').slice(0, 300)}
                  </span>
                </div>
              )}
              {showDate && <div className={styles.date}>{`Edited - ${formatDate(updatedAt!)}`}</div>}
            </div>
          </Link>
          <label className={styles.label} />
        </>
      )}
    </li>
  );
});

export interface ListItemProps extends HTMLAttributes<HTMLLIElement> {
  active?: boolean;
  loading: boolean;
  selected: boolean;
  showDate?: boolean;
  className?: string;
  multiline?: boolean;
  multiselectOn?: boolean;
  data?: SearchableDocument;
  showDescription?: boolean;
  trailingAction?: ReactNode;
  displayCheckForSelected?: boolean;
  agentRunEditorState?: EditorState;
  mode?: 'document' | 'tag' | 'card';
  appearance?: 'default' | 'greyscale';
  agentRunActivityState?: AgentRunActivityState;
  menu?: (data: SearchableDocument) => MenuProps['data'];
}

type LeadingIconConfig = {
  type?: string;
  error?: boolean;
  success?: boolean;
};

const getLeadingIcon = (
  editorState?: EditorState,
  activityState?: AgentRunActivityState,
  iconType?: string
): LeadingIconConfig => {
  switch (true) {
    case activityState === 'errored':
      return {
        type: 'alert-02-solid',
        error: true,
      };
    case editorState === 'review' || activityState === 'awaiting_summary':
      return {
        type: 'tick-02',
        success: true,
      };
    default:
      return {
        type: iconType,
      };
  }
};
